import { TMarkerType } from '@awtg-ltd/react-map-component/dist/constants/common';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { attributesFilters } from '@components/Map/MapControls/Controls/Filters/AssetsFilters/filterItems';

import { TAppRootState } from '@store/reducer';
import { TAppDispatch } from '@store/store';

import { getSiteProfile } from '@api/manageSite';
import { getAssetById } from '@api/map';

import { isEmpty } from '@utils/common';
import QueryClientService from '@utils/queryClientService';
import { clearLocalStorage, getLocalStorage, setLocalStorage } from '@utils/storage';
import { IFilterOoklaDataReturn } from '@components/Map/MapControls/Controls/FiltersOokla/helpers/filterOokla';
import { convertAssetTypesArrayToObject } from '@components/Map/Markers/helpers/convertAssetTypesToMap';

import { TAsset, TAssetProfile, TAssetTypeSubTypeItem } from '@interfaces/assets/asset';
import { ISiteProfile } from '@interfaces/cells/siteProfile';
import { TAssetTypesDTO } from '@interfaces/map/dto/mapDTO';
import { AssetsFiltersDTO, IMapOoklaFiltersDto, PartialDetailedMapFiltersDTO } from '@interfaces/map/dto/mapFiltersDTO';
import { ICoords, IMapBounds, ISiteMarker, TVendor } from '@interfaces/map/map';
import {
	IAssetFilters,
	IAssetTypeForFilter,
	IMapFilterItem,
	ISitesFilters,
	ISitesFilterSectionsState,
} from '@interfaces/map/mapFilters';
import { ISectorsFilterState, ISectorsState } from '@interfaces/map/sectors';
import {
	ESiteAccessAvailability,
	TSiteAccessAvailability,
} from '@components/Map/MapControls/Controls/Filters/AssetsFilters/types';

import { EAssetAcquiredStatuses, TAssetAcquiredStatuses } from '@enums/asset';
import { EControlsType, ESitesFilterSections, EVendorsValues, TEControlsType } from '@enums/common';
import { EColorAssetsBy, EDataType, EMapMode, EPolygonType, TColorAssetsBy, TMapMode, TPolygonType } from '@enums/map';

import { DEFAULT_CLUSTER_ZOOM_LVL, DEFAULT_SECTOR_ZOOM_LEVEL, DEFAULT_ZOOM_LVL } from '@constants/common';
import { CLUSTER_ZOOM_LEVEL, FILTER_THRESHOLDS } from '@constants/queryKeys';
import { noSubTypeOptions } from '@components/routes/AdminPanel/AssetsList/constants';

interface IInitialState {
	activeAssetMarkers: Array<string>;
	activeBoroughs: Array<string>;
	activeCustomPolygons: Array<string>;
	activeSiteMarkers: Array<string>;
	activeSitesVendors: Array<string>;
	appliedAcquiredAssetsTypeFilter: TAssetAcquiredStatuses;
	appliedAssetsFilterValues: AssetsFiltersDTO;
	appliedSitesFiltersValues: ISitesFilters;
	appliedOoklaFilters: IMapOoklaFiltersDto;
	areBoroughsEnabled: boolean;
	assetIdsInsidePolygons: Array<string> | undefined;
	assetTypesSubtypes: Record<string, TAssetTypeSubTypeItem> | null;
	bounds: IMapBounds;
	clusterZoomLvl: number;
	colorAssetsBy: TColorAssetsBy;
	currentAcquiredAssetsTypeFilter: TAssetAcquiredStatuses;
	currentAssetsFilterValues: IAssetFilters;
	defaultCenterCoords: { lat: number; lng: number };
	defaultZoomLvl: number;
	drawTool: TPolygonType;
	editingAsset: TAsset | null;
	editingSite: ISiteMarker | null;
	infoWinPosition?: { x: number; y: number } | null;
	isAdminPanelAvailable: boolean;
	isAcquiredAssetsShownApplied: boolean;
	isAcquiredAssetsShownCurrent: boolean;
	isApplyFiltersAvailable: boolean;
	isFetchingMarkers: boolean;
	isMapDataUpdateNeeded: boolean;
	isMapLoaded?: boolean;
	isOwnersOfRentedAssetsShown: boolean;
	isSearchMarker: boolean;
	isShiftHeld: boolean;
	isShowSiteIds: boolean;
	isCellIdsVisible: boolean;
	lastUsedTabSection: string;
	mapMode: TMapMode;
	markerForShortInfo: Partial<TAsset | ISiteProfile> | null;
	markerType: TMarkerType | null;
	pinnedFilter: TEControlsType | null;
	rightSideControls: { [x: string]: boolean };
	searchValue: string;
	sectors: ISectorsState;
	selectedCustomPolygonForView: string | number | null;
	selectedSiteIdsByShiftKey: Array<string>;
	selectedVendorOnSitesCard: TVendor | null;
	showMarkerFullInfo: boolean;
	showMarkerShortInfo: boolean;
	showingCustomPolygonIds: Array<string>;
	siteAccessAvailable: TSiteAccessAvailability;
	siteIdsWithinActivePolygons: Array<string>;
	sitesFilterSectionsState: ISitesFilterSectionsState;
	usedAssetTypeIds: Array<string>;
	zoomLvl: number;
	visibleSiteIds: Array<string>;
	ooklaFilteredData: IFilterOoklaDataReturn;
}

interface IViewOnMapInterface {
	marker?: TAsset | TAssetProfile | ISiteProfile;
	assetId?: string;
}

export const InitialFilterValues = {
	assetType: [],
	attributes: attributesFilters,
	detailed: undefined,
	isMyLinked: false,
};

export const initialRightControlsState = {
	[EControlsType.filters]: false,
	[EControlsType.filtersOokla]: false,
	[EControlsType.mapSettings]: false,
	[EControlsType.owners]: false,
	[EControlsType.customPolygonControls]: false,
	[EControlsType.customPolygonList]: false,
	[EControlsType.createAssetMode]: false,
	[EControlsType.dashboardKpi]: false,
	[EControlsType.sectors]: false,
};

export const initialSitesFilters = {
	siteType: [],
	technologies: [],
	vendor: [EVendorsValues.nokia, EVendorsValues.limenet, EVendorsValues.mavenir],
};

function getClusterZoomLvl() {
	try {
		return getLocalStorage(CLUSTER_ZOOM_LEVEL);
	} catch (e) {
		return DEFAULT_CLUSTER_ZOOM_LVL;
	}
}

export const initialState: IInitialState = {
	activeAssetMarkers: [],
	activeBoroughs: [],
	activeCustomPolygons: [],
	activeSiteMarkers: [],
	activeSitesVendors: [],
	appliedAcquiredAssetsTypeFilter: EAssetAcquiredStatuses.all,
	appliedAssetsFilterValues: {} as AssetsFiltersDTO,
	appliedOoklaFilters: {} as IMapOoklaFiltersDto,
	appliedSitesFiltersValues: initialSitesFilters,
	areBoroughsEnabled: false,
	assetIdsInsidePolygons: [],
	assetTypesSubtypes: null,
	bounds: {} as IMapBounds,
	clusterZoomLvl: getClusterZoomLvl() || DEFAULT_CLUSTER_ZOOM_LVL,
	colorAssetsBy: EColorAssetsBy.none,
	currentAcquiredAssetsTypeFilter: EAssetAcquiredStatuses.all,
	currentAssetsFilterValues: InitialFilterValues,
	defaultCenterCoords: { lat: 51.56811673221142, lng: -0.2939556426322554 },
	defaultZoomLvl: DEFAULT_ZOOM_LVL,
	drawTool: EPolygonType.polygon,
	editingAsset: {} as TAsset,
	editingSite: null,
	infoWinPosition: null,
	isAdminPanelAvailable: false,
	isAcquiredAssetsShownApplied: false,
	isAcquiredAssetsShownCurrent: false,
	isApplyFiltersAvailable: false,
	isFetchingMarkers: false,
	isMapDataUpdateNeeded: false,
	isMapLoaded: false,
	isOwnersOfRentedAssetsShown: false,
	isSearchMarker: false,
	isShiftHeld: false,
	isShowSiteIds: false,
	lastUsedTabSection: '',
	mapMode: EMapMode.view,
	markerForShortInfo: null,
	markerType: null,
	pinnedFilter: null,
	rightSideControls: initialRightControlsState,
	searchValue: '',
	sectors: {
		isSectorsShown: false,
		zoomLevel: DEFAULT_SECTOR_ZOOM_LEVEL,
		filters: null,
		selectedSiteIds: [],
		isSelectiveSectorsShown: false,
	},
	selectedCustomPolygonForView: null,
	selectedSiteIdsByShiftKey: [],
	selectedVendorOnSitesCard: null,
	showMarkerFullInfo: false,
	showMarkerShortInfo: false,
	showingCustomPolygonIds: [],
	siteAccessAvailable: ESiteAccessAvailability.all,
	siteIdsWithinActivePolygons: [],
	sitesFilterSectionsState: {
		[ESitesFilterSections.technologies]: true,
		[ESitesFilterSections.siteTypes]: false,
	},
	usedAssetTypeIds: [],
	zoomLvl: DEFAULT_ZOOM_LVL,
	visibleSiteIds: [],
	isCellIdsVisible: false,
	ooklaFilteredData: { assetRelatedData: {}, coverageRelatedData: {} },
};

const map = createSlice({
	name: 'map',
	initialState,
	reducers: {
		setAssetTypesFilter: (state, action: PayloadAction<IAssetTypeForFilter[]>) => {
			state.currentAssetsFilterValues.assetType = action.payload;
		},
		resetAssetTypesFilter: (state) => {
			state.currentAssetsFilterValues.assetType = [];

			delete state.appliedAssetsFilterValues.assetType;
			delete state.appliedAssetsFilterValues.assetSubType;
		},
		setAttributesFilter: (state, action: PayloadAction<{ attributeIdOrName: string }>) => {
			const { currentAssetsFilterValues: { attributes } } = state;
			const { attributeIdOrName } = action.payload;

			state.currentAssetsFilterValues.attributes = attributes.map(({ id, items, ...rest }) => {
				let newItems;

				if (items?.length) {
					newItems = items.map(item => {
						if ((item as IMapFilterItem).id === attributeIdOrName) {
							return { ...item, isSelected: !(item as IMapFilterItem).isSelected };
						}
						if ((item as IMapFilterItem).name === attributeIdOrName) {
							return { ...item };
						}

						return item;
					});
				}

				return { ...rest, id, items: newItems || items };
			});
		},
		setSiteAccessAvailability: (state, action: PayloadAction<TSiteAccessAvailability>) => {
			state.siteAccessAvailable = action.payload;
		},
		setColorAssetsBy: (state, action: PayloadAction<TColorAssetsBy>) => {
			state.colorAssetsBy = action.payload;
		},
		resetAttributesFilter: (state) => {
			state.currentAssetsFilterValues.attributes = attributesFilters;
			state.colorAssetsBy = EColorAssetsBy.none;
			state.siteAccessAvailable = ESiteAccessAvailability.all;

			delete state.appliedAssetsFilterValues.assetSiteAvailabilityStatus;
			delete state.appliedAssetsFilterValues.powerSupplyAvailabilityStatus;
			delete state.appliedAssetsFilterValues.backhaulAccessibilityStatus;
			delete state.appliedAssetsFilterValues.isAvailable;
		},
		setZoomLvl: (state, action: PayloadAction<number>) => {
			state.zoomLvl = action.payload;
		},
		setDetailedFilters: (state, action: PayloadAction<PartialDetailedMapFiltersDTO>) => {
			state.currentAssetsFilterValues.detailed = action.payload;
		},
		resetDetailedFilters: (state) => {
			delete state.currentAssetsFilterValues.detailed;
			delete state.appliedAssetsFilterValues.detailed;
		},
		setBounds: (state, action: PayloadAction<IMapBounds>) => {
			state.bounds = action.payload;
		},
		applyFilterValues: (state, action: PayloadAction<{ assetOwnerIds?: string[] }>) => {
			const assetsByColor = state.currentAssetsFilterValues.attributes
				.reduce<Record<string, string[]>>((acc, curr) => {
					const selectedColors = curr.items.reduce<Array<string>>((acc, curr) => {
						if (curr.isSelected) acc.push(curr.color);

						return acc;
					}, []);

					if (selectedColors.length) acc[curr.fieldName] = selectedColors;

					return acc;
				}, {});

			const isAvailableSites = state.siteAccessAvailable === 'Yes'
				? { isAvailable: 'true' }
				: state.siteAccessAvailable === 'No'
					? { isAvailable: 'false' }
					: {};

			const selectedAssetTypes = state.currentAssetsFilterValues
				.assetType.reduce<TAssetTypesDTO>((acc, curr) => {
					const isSubtypeSelected = curr?.subTypes?.some(({ isSelected }) => isSelected);

					if (isSubtypeSelected && curr?.subTypes?.length) {
						const subTypes = curr?.subTypes
							.filter(({ isSelected }) => isSelected)
							.map(({ subType, id }) => {
								if (subType === noSubTypeOptions.value) return subType;

								return id;
							});

						acc.push({ [curr.id]: { subTypes } });
					} else {
						if (curr.isSelected) acc.push(curr.id);
					}

					return acc;
				}, []);

			const { height, availabilityReviewDate, acceptablePrice } = state.currentAssetsFilterValues.detailed || {};
			const siteAvailabilityReviewDate = availabilityReviewDate
				? {
					siteAvailabilityReviewDate: {
						from: availabilityReviewDate.fromDate,
						to: availabilityReviewDate.toDate,
					},
				}
				: {};
			const withoutHeight = state?.currentAssetsFilterValues?.detailed?.withoutHeight !== undefined
				? { withoutHeight: state.currentAssetsFilterValues.detailed.withoutHeight }
				: {};

			const ownerId = action.payload?.assetOwnerIds;

			const { height: heightThreshold, pricing: pricingThreshold } = QueryClientService.getQueryData(
				[FILTER_THRESHOLDS],
			) || {};

			const isHeightEqualToMinMax = height?.from === heightThreshold?.from && height?.to === heightThreshold?.to;
			const isPricingEqualToMinMax = acceptablePrice?.from === pricingThreshold?.from
				&& acceptablePrice?.to === pricingThreshold?.to;

			const assetOwnerIds = isEmpty(action.payload)
				? { ownerId: state.appliedAssetsFilterValues.ownerId }
				: ownerId?.length ? { ownerId } : {};

			state.appliedAssetsFilterValues = {
				...assetsByColor,
				...isAvailableSites,
				...assetOwnerIds,
				assetType: selectedAssetTypes,
				detailed: {
					...height?.to
					&& !isHeightEqualToMinMax
					&& state?.currentAssetsFilterValues?.detailed?.withoutHeight !== true
						? {
							height: {
								from: height.from,
								to: height.to,
							},
						}
						: {},
					...acceptablePrice?.to && !isPricingEqualToMinMax
						? {
							indicativeAcceptablePricing: {
								from: acceptablePrice.from,
								to: acceptablePrice.to,
							},
						}
						: {},
					...siteAvailabilityReviewDate,
					...withoutHeight,
				},
			};
		},
		resetAppliedAssetFilters: (state) => {
			state.appliedAssetsFilterValues = {};
		},
		removeSelectedActiveAssetMarker: (state, action: PayloadAction<string>) => {
			state.activeAssetMarkers = state.activeAssetMarkers.filter((id) => id !== action.payload);
		},
		setActiveAssetMarker: (state, action: PayloadAction<{
			asset: TAsset;
			isSearch?: boolean;
			isShift: boolean;
			showMarkerShortInfo?: boolean;
		}>) => {
			const { asset, isShift } = action.payload;

			const isAlreadyActive = state.activeAssetMarkers.some((id) => id === asset.id);

			state.isSearchMarker = !!action.payload.isSearch;

			if (isAlreadyActive && isShift) {
				state.activeAssetMarkers = state.activeAssetMarkers.filter((id) => id !== asset.id);

				return;
			} else if (isAlreadyActive && !isShift) {
				return;
			}

			state.showMarkerShortInfo = !!action.payload.showMarkerShortInfo;
			state.activeAssetMarkers = isShift
				? [...state.activeAssetMarkers, asset.id]
				: [asset.id];
			state.activeBoroughs = [];
		},
		setActiveSiteMarker: (state, action: PayloadAction<{
			site: ISiteProfile;
			isSearch?: boolean;
			isShift: boolean;
			showSiteShortInfo?: boolean;
		}>) => {
			const { site, isShift } = action.payload;

			const isAlreadyActive = state.activeSiteMarkers.some((id) => id === site.id);

			state.isSearchMarker = !!action.payload.isSearch;

			if (isAlreadyActive && isShift) {
				state.activeSiteMarkers = state.activeSiteMarkers.filter((id) => id !== site.id);

				return;
			} else if (isAlreadyActive && !isShift) {
				return;
			}

			state.showMarkerShortInfo = !!action.payload.showSiteShortInfo;
			state.activeSiteMarkers = isShift
				? [...state.activeSiteMarkers, site.id]
				: [site.id];
			state.activeBoroughs = [];

			if (action.payload.isSearch) {
				state.showMarkerFullInfo = true;
			}
		},
		setDefaultMarkerView: (state, action: PayloadAction<TAsset | ISiteProfile>) => {
			const { clusterZoomLvl } = state;
			const { lat, lng } = action.payload;
			const zoom = clusterZoomLvl + 1;

			state.zoomLvl = zoom;
			state.defaultZoomLvl = zoom;
			state.defaultCenterCoords = {
				lat: lat,
				lng: lng,
			};
		},
		resetActiveMarkers: (state) => {
			state.showMarkerShortInfo = false;
			state.activeAssetMarkers = [];
			state.activeSiteMarkers = [];
		},
		setShift: (state, action: PayloadAction<boolean>) => {
			state.isShiftHeld = action.payload;
		},
		setMarkerForShortInfo: (
			state,
			action: PayloadAction<Partial<TAsset> | Partial<ISiteProfile> | null>,
		) => {
			state.showMarkerShortInfo = !!action.payload;
			state.markerForShortInfo = action.payload;
		},
		setInfoWinPosition: (state, action: PayloadAction<{ x: number; y: number } | null>) => {
			state.infoWinPosition = action.payload;
		},
		setBoroughsState: (state, action: PayloadAction<boolean>) => {
			state.areBoroughsEnabled = action.payload;
		},
		setActivePolygon: (state, action: PayloadAction<{ id: string; isCustom: boolean }>) => {
			const { id, isCustom } = action.payload;
			const { isShiftHeld } = state;

			if (!isShiftHeld) {
				if (isCustom) state.activeCustomPolygons = [id];
				else state.activeBoroughs = [id];
			} else if (!state.activeBoroughs.find(activePolygonId => activePolygonId === id) && !isCustom) {
				state.activeBoroughs.push(id);
			} else if (
				!state.activeCustomPolygons.find(activeCustomPolygonId => activeCustomPolygonId === id) && isCustom
			) {
				state.activeCustomPolygons.push(id);
			}

			state.activeAssetMarkers = [];
		},
		removeCustomPolygonFromActive: (state, action: PayloadAction<string>) => {
			state.activeCustomPolygons = state.activeCustomPolygons.filter(polygonId => polygonId !== action.payload);
		},
		resetActiveBoroughs: (state) => {
			state.activeBoroughs = [];
		},
		resetActiveCustomPolygons: (state) => {
			state.activeCustomPolygons = [];
		},
		setClusterZoomLevel: (state, action: PayloadAction<number>) => {
			state.clusterZoomLvl = action.payload;
			setLocalStorage(CLUSTER_ZOOM_LEVEL, action.payload);
		},
		resetClusterZoomLvl: (state) => {
			state.clusterZoomLvl = DEFAULT_CLUSTER_ZOOM_LVL;
			clearLocalStorage(CLUSTER_ZOOM_LEVEL);
		},
		setMapMode: (state, action: PayloadAction<TMapMode>) => {
			state.mapMode = action.payload;
		},
		setDrawTool: (state, action: PayloadAction<TPolygonType>) => {
			state.drawTool = action.payload;
		},
		setWithoutHeightParam: (state, action: PayloadAction<boolean>) => {
			state.currentAssetsFilterValues.detailed = {
				...state.currentAssetsFilterValues.detailed,
				withoutHeight: action.payload,
			};
		},
		setShowingCustomPolygonIds: (state, action: PayloadAction<Array<string>>) => {
			state.showingCustomPolygonIds = action.payload;
		},
		hideCustomPolygonsOnMap: (state, action: PayloadAction<string[]>) => {
			state.showingCustomPolygonIds = state.showingCustomPolygonIds.filter(p => !action.payload.includes(p));
		},
		resetShowingCustomPolygonIds: (state) => {
			state.showingCustomPolygonIds = [];
		},
		setRightSideControls: (state, action: PayloadAction<TEControlsType>) => {
			const isAlreadyOpened = state.rightSideControls[action.payload];

			if (isAlreadyOpened) state.rightSideControls = initialRightControlsState;
			else {
				const newState: Record<string, boolean> = {};

				for (const key in state.rightSideControls) {
					newState[key] = key === action.payload;
				}

				state.rightSideControls = newState;
			}
		},
		setRightSideControlsByFlag: (state, action: PayloadAction<{ fieldName: TEControlsType; isOpen: boolean }>) => {
			if (action.payload.isOpen) {
				const newState: Record<string, boolean> = {};

				for (const key in state.rightSideControls) {
					newState[key] = key === action.payload.fieldName;
				}

				state.rightSideControls = newState;
			} else state.rightSideControls[action.payload.fieldName] = action.payload.isOpen;
		},
		resetRightSideControls: (state) => {
			state.rightSideControls = initialRightControlsState;
			state.pinnedFilter = null;
		},
		setAssetLocationEditMode: (state, action: PayloadAction<TAsset>) => {
			state.editingAsset = action.payload;
		},
		setSiteLocationEditMode: (state, action: PayloadAction<ISiteMarker>) => {
			state.editingSite = action.payload;
		},
		resetEditingAsset: (state) => {
			state.editingAsset = {} as TAsset;
		},
		setApplyFiltersAvailableState: (state, action: PayloadAction<boolean>) => {
			state.isApplyFiltersAvailable = action.payload;
		},
		setAssetIdsInsidePolygons: (state, action: PayloadAction<Array<string> | undefined>) => {
			state.assetIdsInsidePolygons = action.payload;
		},
		resetAssetIdsInsidePolygons: (state) => {
			state.assetIdsInsidePolygons = [];
		},
		setSelectedCustomPolygonForView: (state, action: PayloadAction<string | number>) => {
			state.selectedCustomPolygonForView = action.payload;
		},
		resetSelectedCustomPolygonForView: (state) => {
			state.selectedCustomPolygonForView = null;
		},
		setIsMapDataUpdateNeeded: (state, action: PayloadAction<boolean>) => {
			state.isMapDataUpdateNeeded = action.payload;
		},
		setMarkerType: (state, action: PayloadAction<TMarkerType>) => {
			state.markerType = action.payload;
		},
		setSitesFilters: (state, action: PayloadAction<Partial<ISitesFilters>>) => {
			state.appliedSitesFiltersValues = {
				...state.appliedSitesFiltersValues,
				...action.payload,
			};
		},
		resetSitesFilters: (state) => {
			state.appliedSitesFiltersValues = initialSitesFilters;
		},
		setSiteIdsInsidePolygons: (state, action: PayloadAction<Array<string>>) => {
			state.siteIdsWithinActivePolygons = action.payload;
		},
		resetSiteIdsInsidePolygons: (state) => {
			state.siteIdsWithinActivePolygons = [];
		},
		setPinnedFilter: (state, action: PayloadAction<TEControlsType | null>) => {
			state.pinnedFilter = action.payload;
		},
		applyShowAssetsLinkedToSites: (state, action: PayloadAction<boolean>) => {
			state.appliedAssetsFilterValues.isMyLinked = action.payload;
		},
		setShowAssetsLinkedToSitesState: (state, action: PayloadAction<boolean>) => {
			state.currentAssetsFilterValues.isMyLinked = action.payload;
		},
		setAppliedAcquiredAssetsTypeFilter: (state, action: PayloadAction<TAssetAcquiredStatuses>) => {
			state.appliedAcquiredAssetsTypeFilter = action.payload;
		},
		setCurrentAcquiredAssetsTypeFilter: (state, action: PayloadAction<TAssetAcquiredStatuses>) => {
			state.currentAcquiredAssetsTypeFilter = action.payload;
		},
		setIsAcquiredAssetsShownApplied: (state, action: PayloadAction<boolean>) => {
			state.isAcquiredAssetsShownApplied = action.payload;
		},
		setIsAcquiredAssetsShownCurrent: (state, action: PayloadAction<boolean>) => {
			state.isAcquiredAssetsShownCurrent = action.payload;
		},
		resetAssetStateFilter: (state) => {
			state.appliedAcquiredAssetsTypeFilter = EAssetAcquiredStatuses.all;
			state.appliedAssetsFilterValues.isMyLinked = false;
			state.currentAcquiredAssetsTypeFilter = EAssetAcquiredStatuses.all;
			state.currentAssetsFilterValues.isMyLinked = false;
			state.isAcquiredAssetsShownApplied = false;
			state.isAcquiredAssetsShownCurrent = false;
		},
		setLastUsedTabSection: (state, action: PayloadAction<string>) => {
			state.lastUsedTabSection = action.payload;
		},
		setIsOwnersOfRentedAssetsShown: (state, action: PayloadAction<boolean>) => {
			state.isOwnersOfRentedAssetsShown = action.payload;
		},
		setActiveSitesVendors: (state, action: PayloadAction<Array<string>>) => {
			state.activeSitesVendors = action.payload;
		},
		setSelectedVendorOnSitesCard: (state, action: PayloadAction<TVendor | null>) => {
			state.selectedVendorOnSitesCard = action.payload;
		},
		resetEditingMarkers: (state) => {
			state.editingAsset = null;
			state.editingSite = null;
			state.activeSiteMarkers = [];
		},
		hidePolygonOnMap: (state, action: PayloadAction<string>) => {
			const currentPolygons = state.showingCustomPolygonIds;

			state.showingCustomPolygonIds = currentPolygons.filter((id) => id !== action.payload);
		},
		setIsShowSiteIds: (state, action: PayloadAction<boolean>) => {
			state.isShowSiteIds = action.payload;
		},
		setIsFetchingMarkers: (state, action: PayloadAction<boolean>) => {
			state.isFetchingMarkers = action.payload;
		},
		setIsMapLoaded: (state, action: PayloadAction<boolean>) => {
			state.isMapLoaded = action.payload;
		},
		setSelectedSiteIdsByShiftKey: (state, action: PayloadAction<{
			siteId: string;
			isShift: boolean;
		}>) => {
			const { siteId, isShift } = action.payload;
			const isAlreadySelected = state.selectedSiteIdsByShiftKey.some((id) => id === siteId);

			if (isShift) {
				if (isAlreadySelected) {
					state.selectedSiteIdsByShiftKey = state.selectedSiteIdsByShiftKey.filter((id) => (
						id !== siteId
					));

					return;
				}

				state.selectedSiteIdsByShiftKey = [
					...state.selectedSiteIdsByShiftKey,
					siteId,
				];
			} else {
				state.selectedSiteIdsByShiftKey = [siteId];
			}
		},
		resetSelectedSiteIdsByShiftKey: (state) => {
			state.selectedSiteIdsByShiftKey = [];
		},
		setShowMarkerFullInfo: (state, action: PayloadAction<boolean>) => {
			state.showMarkerFullInfo = action.payload;
		},
		setShowMarkerShortInfo: (state, action: PayloadAction<boolean>) => {
			state.showMarkerShortInfo = action.payload;
		},
		setActiveSiteMarkers: (state, action: PayloadAction<Array<string>>) => {
			state.activeSiteMarkers = action.payload;
		},
		setActiveCustomPolygons: (state, action: PayloadAction<Array<string>>) => {
			state.activeCustomPolygons = action.payload;
		},
		setAssetTypesSubtypes: (state, action: PayloadAction<Array<TAssetTypeSubTypeItem>>) => {
			state.assetTypesSubtypes = convertAssetTypesArrayToObject(action.payload);
		},
		toggleSectors: (state, action: PayloadAction<boolean>) => {
			state.sectors.isSectorsShown = action.payload;
		},
		setSitesFilterSectionsState: (state, action: PayloadAction<ISitesFilterSectionsState>) => {
			state.sitesFilterSectionsState = action.payload;
		},
		setSectorsFilters: (state, action: PayloadAction<ISectorsFilterState | null>) => {
			state.sectors.filters = action.payload || { technologies: [], bands: [] };
		},
		setVisibleSiteIds: (state, action: PayloadAction<Array<string>>) => {
			state.visibleSiteIds = action.payload;
		},
		setCellIdsVisibility: (state, action: PayloadAction<boolean>) => {
			state.isCellIdsVisible = action.payload;
		},
		addSiteIdToRenderSector: (state, action: PayloadAction<string>) => {
			state.sectors.selectedSiteIds.push(action.payload);
			state.sectors.isSelectiveSectorsShown = true;
		},
		removeSiteIdFromRenderSector: (state, action: PayloadAction<string>) => {
			state.sectors.selectedSiteIds = state.sectors.selectedSiteIds.filter((id) => id !== action.payload);
			state.sectors.isSelectiveSectorsShown = !!state.sectors.selectedSiteIds.length;
		},
		clearSelectedSiteIdsToRenderSector: (state) => {
			state.sectors.selectedSiteIds = [];
			state.sectors.isSelectiveSectorsShown = false;
		},
		setAppliedOoklaFilters: (state, action: PayloadAction<IMapOoklaFiltersDto>) => {
			state.appliedOoklaFilters = action.payload;
		},
		resetAppliedOoklaFilters: (state) => {
			state.appliedOoklaFilters = {};
		},
		setOoklaFilteredData: (state, action: PayloadAction<IFilterOoklaDataReturn>) => {
			state.ooklaFilteredData = action.payload;
		},
		clearOoklaDataFiltered: (state) => {
			state.ooklaFilteredData = { assetRelatedData: {}, coverageRelatedData: {} };
		},
		disableOoklaVisibility: (state) => {
			state.appliedOoklaFilters = {
				...state.appliedOoklaFilters,
				isOoklaInfo: false,
			};
		},
		setIsAdminPanelAvailable: (state, action: PayloadAction<boolean>) => {
			state.isAdminPanelAvailable = action.payload;
		},
	},
});

export const {
	addSiteIdToRenderSector,
	applyFilterValues,
	applyShowAssetsLinkedToSites,
	clearOoklaDataFiltered,
	clearSelectedSiteIdsToRenderSector,
	disableOoklaVisibility,
	hideCustomPolygonsOnMap,
	hidePolygonOnMap,
	removeCustomPolygonFromActive,
	removeSelectedActiveAssetMarker,
	removeSiteIdFromRenderSector,
	resetActiveBoroughs,
	resetActiveCustomPolygons,
	resetActiveMarkers,
	resetAppliedAssetFilters,
	resetAppliedOoklaFilters,
	resetAssetIdsInsidePolygons,
	resetAssetStateFilter,
	resetAssetTypesFilter,
	resetAttributesFilter,
	resetClusterZoomLvl,
	resetDetailedFilters,
	resetEditingAsset,
	resetEditingMarkers,
	resetRightSideControls,
	resetSelectedCustomPolygonForView,
	resetSelectedSiteIdsByShiftKey,
	resetShowingCustomPolygonIds,
	resetSiteIdsInsidePolygons,
	resetSitesFilters,
	setActiveAssetMarker,
	setActiveCustomPolygons,
	setActivePolygon,
	setActiveSiteMarker,
	setActiveSiteMarkers,
	setActiveSitesVendors,
	setAppliedAcquiredAssetsTypeFilter,
	setAppliedOoklaFilters,
	setApplyFiltersAvailableState,
	setAssetIdsInsidePolygons,
	setAssetLocationEditMode,
	setAssetTypesFilter,
	setAssetTypesSubtypes,
	setAttributesFilter,
	setBoroughsState,
	setBounds,
	setCellIdsVisibility,
	setClusterZoomLevel,
	setColorAssetsBy,
	setCurrentAcquiredAssetsTypeFilter,
	setDefaultMarkerView,
	setDetailedFilters,
	setDrawTool,
	setInfoWinPosition,
	setIsAcquiredAssetsShownApplied,
	setIsAcquiredAssetsShownCurrent,
	setIsAdminPanelAvailable,
	setIsFetchingMarkers,
	setIsMapDataUpdateNeeded,
	setIsMapLoaded,
	setIsOwnersOfRentedAssetsShown,
	setIsShowSiteIds,
	setLastUsedTabSection,
	setMapMode,
	setMarkerForShortInfo,
	setMarkerType,
	setOoklaFilteredData,
	setPinnedFilter,
	setRightSideControls,
	setRightSideControlsByFlag,
	setSectorsFilters,
	setSelectedCustomPolygonForView,
	setSelectedSiteIdsByShiftKey,
	setSelectedVendorOnSitesCard,
	setShift,
	setShowAssetsLinkedToSitesState,
	setShowMarkerFullInfo,
	setShowMarkerShortInfo,
	setShowingCustomPolygonIds,
	setSiteAccessAvailability,
	setSiteIdsInsidePolygons,
	setSiteLocationEditMode,
	setSitesFilterSectionsState,
	setSitesFilters,
	setVisibleSiteIds,
	setWithoutHeightParam,
	setZoomLvl,
	toggleSectors,
} = map.actions;

export const resetAllFilters = () => (dispatch: TAppDispatch) => {
	dispatch(resetDetailedFilters());
	dispatch(resetAttributesFilter());
	dispatch(resetAssetTypesFilter());
	dispatch(resetAssetStateFilter());
	dispatch(resetSitesFilters());
	dispatch(resetAppliedAssetFilters());
	dispatch(resetAppliedOoklaFilters());
};

export const viewOnMap = ({
	marker,
	assetId,
}: IViewOnMapInterface) =>
	async (dispatch: TAppDispatch, getState: () => TAppRootState) => {
		const { map: { markerType } } = getState();

		if (markerType !== EDataType.assets) dispatch(setMarkerType(EDataType.assets));

		const asset = marker || await getAssetById(assetId);

		dispatch(setDefaultMarkerView(asset));
		dispatch(setActiveAssetMarker({
			asset,
			isShift: false,
			showMarkerShortInfo: false,
		}));
		dispatch(setShowMarkerFullInfo(true));
	};

export const viewSiteOnMap = (siteId: string) =>
	async (dispatch: TAppDispatch, getState: () => TAppRootState) => {
		const { map: { markerType } } = getState();

		if (markerType !== EDataType.sites) dispatch(setMarkerType(EDataType.sites));

		const site = await getSiteProfile(siteId);

		dispatch(setDefaultMarkerView(site));
		dispatch(setActiveSiteMarker({
			site,
			isShift: false,
		}));
		dispatch(setShowMarkerFullInfo(true));
	};

export const centerMapOnSite = (siteId: string, panTo?: (coords: ICoords, zoomLevel: number) => void) => {
	return async (dispatch: TAppDispatch, getState: () => TAppRootState) => {
		const { map: { activeSiteMarkers, clusterZoomLvl, markerType } } = getState();

		if (markerType !== EDataType.sites) dispatch(setMarkerType(EDataType.sites));

		const site = await getSiteProfile(siteId);

		dispatch(setDefaultMarkerView(site));

		if (!activeSiteMarkers.includes(siteId)) {
			dispatch(setActiveSiteMarker({
				site,
				isShift: false,
				isSearch: true,
			}));
			dispatch(setShowMarkerFullInfo(true));
		}

		if (panTo) panTo({ lat: site.lat, lng: site.lng }, clusterZoomLvl + 1);
	};
};

export default map.reducer;
